/**
 * The source tabs the add-players panel offers.
 *
 * EVERY pipeline offers all three — Suggested, Watchlists, My Roster —
 * whatever the board's `pipelineTemplate`. The motion is deliberately not a
 * filter here.
 *
 * SE-5490 originally derived the tab set from the board's recruitment motion:
 * a NEW_ACQUISITIONS pipeline dropped My Roster (you are signing players you
 * do not have), and ACADEMY_PROMOTIONS / OUTGOING_TRANSFERS dropped Watchlists
 * (the motion acts on players you already hold). The reasoning describes the
 * typical case, but the exceptions are ordinary work — a player you already
 * track lands in an acquisitions pipeline, an academy prospect is scouted onto
 * a watchlist — and a missing tab left no way to reach them at all. Product
 * retired the rule on 2026-08-12 (Pietra, SE-6913): a tab the user does not
 * need costs a glance, a tab they do need and cannot open costs the workflow.
 *
 * Reintroducing a `pipelineTemplate` fork over the tab set would restore that
 * defect.
 *
 * Suggested is always offered first and is always the default selection.
 * @module features/player-kanban/utils/addPlayersPanelSources
 */

import type { KanbanPipelineTemplate } from "@/generated/graphql";

/** A source the add-players panel can pull candidates from. */
export type AddPlayersSource = "suggested" | "watchlist" | "roster";

/**
 * The tab selected when the panel opens, for every pipeline motion.
 *
 * SE-5523's recommendations are the point of the panel, so Suggested wins the
 * default in every case rather than the panel restoring a last-used tab.
 */
export const DEFAULT_ADD_PLAYERS_SOURCE: AddPlayersSource = "suggested";

/** Every source, in the order the tabs render. */
const ALL_SOURCES: readonly AddPlayersSource[] = [
  "suggested",
  "watchlist",
  "roster",
];

/**
 * Resolve which source tabs a pipeline offers.
 * @param _template - The board's `pipelineTemplate`, accepted so callers keep
 * resolving per board. It does not narrow the result: no motion, and no
 * missing motion, removes a source.
 * @returns Every source, in render order.
 */
export const resolveAddPlayersSources = (
  _template: KanbanPipelineTemplate | null | undefined
): readonly AddPlayersSource[] => ALL_SOURCES;

/**
 * Keep the selected source one the panel renders a tab for.
 *
 * The tab strip and the body below it must agree: a selection outside
 * `available` renders a body no tab points at. Since every pipeline offers
 * every source, nothing in the app produces that state today — this holds the
 * agreement rather than serving a live branch.
 * @param selected - The currently selected source.
 * @param available - The sources this pipeline offers.
 * @returns `selected` when it is still offered, otherwise
 * {@link DEFAULT_ADD_PLAYERS_SOURCE}.
 */
export const resolveSelectedSource = (
  selected: AddPlayersSource,
  available: readonly AddPlayersSource[]
): AddPlayersSource =>
  available.includes(selected) ? selected : DEFAULT_ADD_PLAYERS_SOURCE;

/*
 * `shouldStartCollapsed(cardCount)` lived here until 2026-08-08. It opened the
 * panel on an EMPTY pipeline, which suited the inline RAIL it was written for
 * (SE-5490): the rail yielded width back to the board once cards existed. Once
 * SE-6554 made the panel an overlay drawer, that default covered the empty
 * board behind a pointer-swallowing backdrop instead of helping it. The panel
 * no longer opens itself, so nothing keys off the card count.
 */
